import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
  }, []);

  // Verify user's approval status on initial load and periodically
  const verifyUser = useCallback(async (savedToken) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${savedToken}`
        }
      });
      
      if (response.status === 403) {
        // User is pending or rejected
        const data = await response.json();
        toast.error(data.detail || 'Access denied');
        logout();
        return false;
      }
      
      if (!response.ok) {
        logout();
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error verifying user:', error);
      return true; // Don't logout on network errors
    }
  }, [logout]);

  useEffect(() => {
    // Check for saved token on mount
    const savedToken = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('authUser');
    
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      // Verify user on mount
      verifyUser(savedToken);
    }
    setLoading(false);
  }, [verifyUser]);

  // Periodically verify user status (every 60 seconds when logged in)
  useEffect(() => {
    if (!token) return;
    
    const interval = setInterval(() => {
      verifyUser(token);
    }, 60000); // Check every 60 seconds
    
    return () => clearInterval(interval);
  }, [token, verifyUser]);

  const login = (tokenData, userData) => {
    setToken(tokenData);
    setUser(userData);
    localStorage.setItem('authToken', tokenData);
    localStorage.setItem('authUser', JSON.stringify(userData));
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('authUser', JSON.stringify(userData));
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    login,
    logout,
    updateUser,
    verifyUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
